import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PageHeader from '../components/common/PageHeader';
import { ConsoleInput, ConsoleSelect, ConsoleLabel } from '../styles/common/input.styled';
import { Button } from '../styles/common/Button.styled';
import { useApi } from '../hooks/useApi';
import toast from 'react-hot-toast';

const Container = styled.div`display: flex; flex-direction: column; gap: 24px; max-width: 960px;`;

const FormArea = styled.div`display: flex; flex-direction: column; gap: 24px;`;

const Row = styled.div`display: flex; flex-direction: column; gap: 8px; min-width: 0;`;

const Label = ConsoleLabel;

const Input = styled(ConsoleInput)`&[type="file"] { height: auto; padding: 6px; } &::file-selector-button { border: 0; border-radius: 4px; padding: 6px 10px; margin-right: 10px; background: ${({ theme }) => theme.color.primary_50}; color: ${({ theme }) => theme.color.text_color_100}; }`;

const Select = ConsoleSelect;

const TypeTabs = styled.div`display: flex; gap: 2px; padding: 3px; border-radius: 6px; border: 1px solid ${({ theme }) => theme.color.primary_300};`;

const TabButton = styled(Button).attrs({ $variant: "ghost", $size: "sm" })<{ $active?: boolean }>`color: ${({ theme }) => theme.color.text_color_300}; &[aria-pressed="true"] { background: ${({ theme }) => theme.color.primary_50}; color: ${({ theme }) => theme.color.text_color_100}; }`;

const Inline = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const SearchInput = styled(Input)`
  flex: 1;
  min-width: 220px;
`;

const Helper = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.text_color_300};
  line-height: 1.5;
`;

const DropZone = styled.div<{ $drag?: boolean }>`border: 1px dashed ${({ theme, $drag }) => $drag ? theme.color.point_y_100 : theme.color.primary_400}; border-radius: 8px; padding: 16px; background: ${({ theme }) => theme.color.primary_100};`;

const DropTitle = styled.div`margin-bottom: 10px; font-size: 13px; overflow-wrap: anywhere;`;

const TimeButton = styled(Button).attrs({ $variant: "secondary" })``;

const SubmitRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;

  @media (max-width: 640px) {
    justify-content: initial;
  }
`;

const SubmitButton = Button;

const Section = styled.section`border-top: 1px solid ${({ theme }) => theme.color.primary_300}; padding-top: 20px; display: flex; flex-direction: column; gap: 20px; h2 { font-size: 14px; font-weight: 600; }`;
const Participants = styled.div`display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 24px; @media(max-width:1199px) { grid-template-columns: 1fr; }`;
const Times = styled.div`display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 16px; @media(max-width:1199px) { grid-template-columns: 1fr; }`;

// 공통 유틸
const toApiDateTime = (d: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};
const toLocalDateTime = (d: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// 선택 타입
type PhoneSource = 'CONTACT' | 'USER' | 'MANUAL';

interface Option { label: string; value: string; }

const CallManualPage: React.FC = () => {
  const navigate = useNavigate();
  const initialRingingAt = useMemo(() => new Date(), []);

  // sender/receiver 타입
  const [senderType, setSenderType] = useState<PhoneSource>('CONTACT');
  const [receiverType, setReceiverType] = useState<PhoneSource>('USER');

  // 값 보관
  const [senderPhone, setSenderPhone] = useState('');
  const [receiverPhone, setReceiverPhone] = useState('');
  const [callNumber, setCallNumber] = useState('');
  const [ringingAt, setRingingAt] = useState<Date>(initialRingingAt);
  const [dialupAt, setDialupAt] = useState<Date>(new Date(initialRingingAt.getTime() + 10_000));
  const [hangupAt, setHangupAt] = useState<Date | null>(null);
  const [recordDurationMs, setRecordDurationMs] = useState<number | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // 목록 API 훅들
  const { data: contactsData, request: fetchContacts } = useApi<any>();
  const { data: usersData, request: fetchUsers } = useApi<any>();
  const { data: mainPhoneData, request: fetchMainPhones } = useApi<any>();
  const { loading: submitting, request: submitRequest } = useApi<any>();

  // 초기값 로드
  useEffect(() => {
    fetchMainPhones({ url: '/main-phone', method: 'GET', params: { limit: 100 } });
    // 기본 리스트도 미리 로드
    fetchContacts({ url: '/contact/list', method: 'GET', params: { limit: 50, pg: 1 } });
    fetchUsers({ url: '/user', method: 'GET', params: { limit: 50, pg: 1 } });
  }, [fetchContacts, fetchMainPhones, fetchUsers]);

  // 시각 동기화: ringingAt 갱신 시 dialupAt 자동 +10초
  useEffect(() => {
    setDialupAt(new Date(ringingAt.getTime() + 10_000));
  }, [ringingAt]);

  useEffect(() => {
    if (!recordDurationMs || recordDurationMs <= 0) {
      setHangupAt(null);
      return;
    }

    setHangupAt(new Date(dialupAt.getTime() + recordDurationMs));
  }, [dialupAt, recordDurationMs]);

  // 파일 선택 시 길이 측정 → hangupAt 계산(dialupAt + duration)
  const objectUrlRef = useRef<string | null>(null);
  const calcDurationAndSet = useCallback((f: File) => {
    try {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(f);
      objectUrlRef.current = url;
      const audio = new Audio();
      audio.src = url;
      audio.addEventListener('loadedmetadata', () => {
        const durationSec = isFinite(audio.duration) ? audio.duration : 0;
        if (!durationSec || durationSec <= 0) {
          toast.error('음원 길이를 읽지 못했습니다. 파일을 확인해주세요.');
          setRecordDurationMs(null);
          setHangupAt(null);
          return;
        }
        setRecordDurationMs(Math.round(durationSec * 1000));
      }, { once: true });
    } catch (e) {
      console.error(e);
      toast.error('음원 길이 계산 중 오류가 발생했습니다.');
      setRecordDurationMs(null);
      setHangupAt(null);
    }
  }, []);

  useEffect(() => () => { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); }, []);

  // 옵션 변환
  const contactOptions: Option[] = useMemo(() => {
    const list = contactsData?.content || [];
    return list.map((c: any) => ({ label: `${c.name || '-'} · ${c.phone}`, value: c.phone }));
  }, [contactsData]);

  const userOptions: Option[] = useMemo(() => {
    const list = usersData?.content || [];
    return list.map((u: any) => ({ label: `${u.username || '-'} · ${u.phone || '-'}`, value: u.phone }));
  }, [usersData]);

  const mainPhoneOptions: Option[] = useMemo(() => {
    const list = mainPhoneData?.content || [];
    return list.map((m: any) => ({ label: `${m.name ? m.name + ' · ' : ''}${m.phone}`, value: m.phone }));
  }, [mainPhoneData]);

  useEffect(() => {
    if (!callNumber && mainPhoneOptions.length > 0) {
      setCallNumber(mainPhoneOptions[0].value);
    }
  }, [callNumber, mainPhoneOptions]);

  const onSearchContacts = useCallback((q: string) => {
    fetchContacts({ url: '/contact/list', method: 'GET', params: { limit: 50, pg: 1, name: q || undefined } });
  }, [fetchContacts]);

  const onSearchUsers = useCallback((q: string) => {
    fetchUsers({ url: '/user', method: 'GET', params: { limit: 50, pg: 1, searchKeyword: q || undefined } });
  }, [fetchUsers]);

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) {
      setFile(f);
      calcDurationAndSet(f);
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    if (f) {
      setFile(f);
      calcDurationAndSet(f);
    }
  };

  const validForSubmit = useMemo(() => {
    return !!(senderPhone && receiverPhone && callNumber && file && ringingAt && dialupAt && hangupAt);
  }, [senderPhone, receiverPhone, callNumber, file, ringingAt, dialupAt, hangupAt]);

  const handleSubmit = async () => {
    if (!validForSubmit) {
      toast.error('필수 항목을 모두 입력해주세요.');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('senderPhone', senderPhone);
      fd.append('receiverPhone', receiverPhone);
      fd.append('callNumber', callNumber);
      if (file) fd.append('recordFile', file);
      fd.append('ringingAt', toApiDateTime(ringingAt));
      fd.append('dialupAt', toApiDateTime(dialupAt));
      if (hangupAt) fd.append('hangupAt', toApiDateTime(hangupAt));

      const result = await submitRequest({ url: '/call/manual', method: 'POST', data: fd, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('수동 통화가 등록되었습니다.');
      const id = result?.id;
      if (id) {
        navigate(`/statistics/call-history/${id}`);
      }
    } catch (e) {
      console.error(e);
      toast.error('등록 중 오류가 발생했습니다.');
    }
  };

  return (
    <Container>
      <PageHeader title="수동 통화 등록" description="참여자와 녹취 파일을 선택해 통화 이력을 등록하세요." />
      <FormArea>
        <Section><h2>통화 참여자</h2><Participants>
        {/* 발신자 */}
        <Row>
          <Label htmlFor="manual-sender">발신자</Label>
          <FieldGroup>
            <Inline>
              <TypeTabs>
                <TabButton type="button" aria-pressed={senderType==='CONTACT'} onClick={() => setSenderType('CONTACT')}>연락처</TabButton>
                <TabButton type="button" aria-pressed={senderType==='USER'} onClick={() => setSenderType('USER')}>사용자</TabButton>
                <TabButton type="button" aria-pressed={senderType==='MANUAL'} onClick={() => setSenderType('MANUAL')}>수동입력</TabButton>
              </TypeTabs>
              {senderType !== 'MANUAL' && (
                <SearchInput aria-label="발신자 검색" placeholder="검색어 입력" onChange={(e)=> senderType==='CONTACT' ? onSearchContacts(e.target.value) : onSearchUsers(e.target.value)} />
              )}
            </Inline>
            <div>
              {senderType === 'MANUAL' ? (
                <Input id="manual-sender" placeholder="발신자 번호 입력" value={senderPhone} onChange={(e)=> setSenderPhone(e.target.value)} />
              ) : senderType === 'CONTACT' ? (
                <Select id="manual-sender" value={senderPhone} onChange={(e)=> setSenderPhone(e.target.value)}>
                  <option value="">선택하세요</option>
                  {contactOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </Select>
              ) : (
                <Select id="manual-sender" value={senderPhone} onChange={(e)=> setSenderPhone(e.target.value)}>
                  <option value="">선택하세요</option>
                  {userOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </Select>
              )}
            </div>
            <Helper>연락처에서 선택하거나 발신 번호를 직접 입력하세요.</Helper>
          </FieldGroup>
        </Row>

        {/* 수신자 */}
        <Row>
          <Label htmlFor="manual-receiver">수신자</Label>
          <FieldGroup>
            <Inline>
              <TypeTabs>
                <TabButton type="button" aria-pressed={receiverType==='CONTACT'} onClick={() => setReceiverType('CONTACT')}>연락처</TabButton>
                <TabButton type="button" aria-pressed={receiverType==='USER'} onClick={() => setReceiverType('USER')}>사용자</TabButton>
                <TabButton type="button" aria-pressed={receiverType==='MANUAL'} onClick={() => setReceiverType('MANUAL')}>수동입력</TabButton>
              </TypeTabs>
              {receiverType !== 'MANUAL' && (
                <SearchInput aria-label="수신자 검색" placeholder="검색어 입력" onChange={(e)=> receiverType==='CONTACT' ? onSearchContacts(e.target.value) : onSearchUsers(e.target.value)} />
              )}
            </Inline>
            <div>
              {receiverType === 'MANUAL' ? (
                <Input id="manual-receiver" placeholder="수신자 번호 입력" value={receiverPhone} onChange={(e)=> setReceiverPhone(e.target.value)} />
              ) : receiverType === 'CONTACT' ? (
                <Select id="manual-receiver" value={receiverPhone} onChange={(e)=> setReceiverPhone(e.target.value)}>
                  <option value="">선택하세요</option>
                  {contactOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </Select>
              ) : (
                <Select id="manual-receiver" value={receiverPhone} onChange={(e)=> setReceiverPhone(e.target.value)}>
                  <option value="">선택하세요</option>
                  {userOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </Select>
              )}
            </div>
            <Helper>사용자에서 선택하거나 수신 번호를 직접 입력하세요.</Helper>
          </FieldGroup>
        </Row>

        </Participants></Section>
        <Section><h2>대표번호와 녹취 파일</h2>
        {/* 대표번호 */}
        <Row>
          <Label htmlFor="manual-number">대표번호</Label>
          <FieldGroup>
            <Select id="manual-number" value={callNumber} onChange={(e)=> setCallNumber(e.target.value)}>
              {mainPhoneOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </Select>
            <Helper>전화번호 관리에서 사용 중인 대표번호 목록입니다.</Helper>
          </FieldGroup>
        </Row>

        {/* 음원 파일 */}
        <Row>
          <Label htmlFor="manual-file">음원 파일</Label>
          <FieldGroup>
            <DropZone
              $drag={isDragging}
              onDragOver={(e)=>{ e.preventDefault(); setIsDragging(true); }}
              onDragLeave={()=> setIsDragging(false)}
              onDrop={onDrop}
            >
              <DropTitle>{file ? `선택됨: ${file.name}` : '이곳에 드래그 앤 드롭하거나 파일을 선택하세요.'}</DropTitle>
              <Input id="manual-file" type="file" accept="audio/*" onChange={onFileChange} />
            </DropZone>
            <Helper>정상적인 음원 파일을 선택하면 길이를 자동 계산하여 통화 종료 시각을 설정합니다.</Helper>
          </FieldGroup>
        </Row>

        </Section>
        <Section><h2>통화 시각</h2><Times>
        {/* 시간들 */}
        <Row>
          <Label htmlFor="manual-ringing">통화 유입 시각</Label>
          <FieldGroup>
            <Inline>
              <Input
                id="manual-ringing" type="datetime-local"
                step="1"
                value={toApiDateTime(ringingAt)}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val) setRingingAt(new Date(val));
                }}
              />
              <TimeButton type="button" onClick={()=> setRingingAt(new Date())}>현재 시각</TimeButton>
            </Inline>
          </FieldGroup>
        </Row>
        <Row>
          <Label htmlFor="manual-dialup">통화 수신 시각</Label>
          <FieldGroup>
            <Input id="manual-dialup" value={toLocalDateTime(dialupAt)} readOnly />
            <Helper>통화 유입 시각 이후 10초로 자동 설정됩니다.</Helper>
          </FieldGroup>
        </Row>
        <Row>
          <Label htmlFor="manual-hangup">통화 종료 시각</Label>
          <FieldGroup>
            <Input id="manual-hangup" value={hangupAt ? toLocalDateTime(hangupAt) : ''} placeholder="음원 선택 시 자동 설정" readOnly />
            <Helper>수정 불가. 통화 수신 시각 + 음원 길이로 자동 설정됩니다.</Helper>
          </FieldGroup>
        </Row>

        </Times></Section>
        <SubmitRow>
          <SubmitButton disabled={submitting || !validForSubmit} onClick={handleSubmit}>
            {submitting ? '등록 중...' : '수동 통화 등록'}
          </SubmitButton>
        </SubmitRow>
      </FormArea>
    </Container>
  );
};

export default CallManualPage;
